import React from 'react';
import { Instagram, Facebook, Globe, MapPin } from 'lucide-react';

/**
 * Presentational artist profile used by BOTH the public profile page and the
 * admin preview. Pass `imageElement` (e.g. an auth-protected image) OR `imageSrc`.
 */
const ArtistProfileView = ({ artist, imageSrc, imageElement }) => {
  if (!artist) return null;
  const socials = [
    { key: 'instagramUrl', icon: Instagram, label: 'Instagram' },
    { key: 'facebookUrl', icon: Facebook, label: 'Facebook' },
    { key: 'websiteUrl', icon: Globe, label: 'Website' },
  ].filter((s) => artist[s.key]);

  const normalizeUrl = (u) => (u && !/^https?:\/\//i.test(u) ? `https://${u}` : u);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start" data-testid="artist-profile-view">
      <div className="rounded-2xl overflow-hidden bg-[#f6efe0] border shadow-md" style={{ borderColor: '#f0e4c8' }}>
        <div className="aspect-[4/5] w-full">
          {imageElement || (
            imageSrc ? (
              <img src={imageSrc} alt={artist.artworkTitle || artist.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[#b89b6a]">Artwork coming soon</div>
            )
          )}
        </div>
      </div>

      <div>
        <p className="text-xs tracking-[2px] font-semibold uppercase" style={{ color: '#722F37' }}>
          2026 Wine of the Year · Artist
        </p>
        {artist.artworkTitle && (
          <h1 className="mt-2 text-3xl sm:text-4xl font-bold text-gray-900 leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }} data-testid="artist-artwork-title">
            {artist.artworkTitle}
          </h1>
        )}
        <p className="mt-3 text-xl font-semibold" style={{ color: '#722F37' }} data-testid="artist-name">{artist.name}</p>
        {artist.location && (
          <p className="mt-1 text-sm text-gray-500 inline-flex items-center gap-1">
            <MapPin className="w-4 h-4" /> {artist.location}
          </p>
        )}

        {artist.artistStatement && (
          <div className="mt-6">
            <h2 className="text-sm font-bold uppercase tracking-wide text-gray-700 mb-2">About the design</h2>
            <p className="text-gray-700 leading-relaxed whitespace-pre-line">{artist.artistStatement}</p>
          </div>
        )}
        {artist.bio && (
          <div className="mt-6">
            <h2 className="text-sm font-bold uppercase tracking-wide text-gray-700 mb-2">About the artist</h2>
            <p className="text-gray-700 leading-relaxed whitespace-pre-line">{artist.bio}</p>
          </div>
        )}

        {socials.length > 0 && (
          <div className="mt-8 flex flex-wrap gap-3">
            {socials.map(({ key, icon: Icon, label }) => (
              <a
                key={key}
                href={normalizeUrl(artist[key])}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`artist-social-${key}`}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium hover:bg-[#FFFBEA] transition"
                style={{ borderColor: '#e4d3a8', color: '#722F37' }}
              >
                <Icon className="w-4 h-4" /> {label}
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ArtistProfileView;
